import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  addDoc,
  updateDoc,
  doc as docRef
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import { yupResolver } from '@hookform/resolvers/yup';

import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';
import PhotoUpload from '../../components/PhotoUpload';
import Breadcrumb from '../../components/Breadcrumb';

const schema = yup.object({
  nombre: yup.string().required('Nombre requerido'),
  biografia: yup.string().required('Biografía requerida'),
  telefono: yup
    .string()
    .min(8, 'Mínimo 8 caracteres')
    .required('Teléfono requerido'),
  correo: yup.string().email('Correo inválido').required('Correo requerido'),
  contraseña: yup.string().min(6).required('Contraseña requerida'),
  confirmar: yup
    .string()
    .oneOf([yup.ref('contraseña')], 'Las contraseñas no coinciden')
    .required('Confirma tu contraseña'),
  años_experiencia: yup
    .number()
    .typeError('Debe ser un número')
    .min(0, 'Mínimo 0 años')
    .required('Campo requerido'),
  inmobiliaria_id: yup.string().required('Selecciona una inmobiliaria'),
  redes: yup.object({
    facebook: yup.string().url('URL inválida').optional(),
    instagram: yup.string().url('URL inválida').optional()
  }),
  foto: yup.string().optional()
});

export default function AddAsesor() {
  const [inmobiliarias, setInmobiliarias] = useState<{ id: string; nombre: string; inmobiliaria_doc_id: string }[]>([]);
  const [fotoUrl, setFotoUrl] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [contraseña, setContraseña] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const auth = getAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    const fetchInmobiliarias = async () => {
      const snap = await getDocs(collection(db, 'inmobiliaria'));
      const data = snap.docs.map(doc => ({
        id: doc.id,
        nombre: doc.data().nombre,
        inmobiliaria_doc_id: doc.id // Usar el doc.id de Firestore
      }));
      setInmobiliarias(data);
    };
    fetchInmobiliarias();
  }, []);

  const onError = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Campos incompletos',
      text: 'Completa todos los campos con asterisco (*)',
      confirmButtonColor: '#3085d6'
    });
  };

  const getFirebaseErrorMessage = (errorCode: string): string => {
    const errorMessages: { [key: string]: string } = {
      'auth/email-already-in-use': 'El correo electrónico ya está registrado en el sistema.',
      'auth/invalid-email': 'El correo electrónico no es válido.',
      'auth/operation-not-allowed': 'Operación no permitida. Contacta al administrador.',
      'auth/weak-password': 'La contraseña es demasiado débil. Debe tener al menos 6 caracteres.',
      'auth/missing-email': 'Por favor ingresa un correo electrónico.',
      'auth/missing-password': 'Por favor ingresa una contraseña.',
      'auth/invalid-credential': 'Las credenciales proporcionadas no son válidas.',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada.',
      'auth/user-not-found': 'No existe una cuenta con este correo electrónico.',
      'auth/wrong-password': 'La contraseña es incorrecta.',
      'auth/too-many-requests': 'Demasiados intentos fallidos. Por favor intenta más tarde.',
      'auth/network-request-failed': 'Error de conexión. Verifica tu conexión a internet.',
      'permission-denied': 'No tienes permisos para realizar esta acción.',
      'unavailable': 'El servicio no está disponible temporalmente. Intenta más tarde.',
    };

    return errorMessages[errorCode] || 'Ocurrió un error inesperado. Por favor intenta nuevamente.';
  };

  const generarID = (nombre: string, inmobiliaria: string, años: number) => {
    const nombres = nombre.trim().split(/\s+/).slice(0, 3).map(n => n.slice(0, 5).toLowerCase());
    const iniciales = nombres.map(n => n[0]).join('');
    const inicialInm = inmobiliarias.find(i => i.id === inmobiliaria)?.nombre[0].toLowerCase() || 'x';
    const añosStr = años.toString().padStart(2, '0');
    const base = (iniciales + inicialInm + añosStr).slice(0, 8);
    const random = Math.floor(100 + Math.random() * 900).toString();
    return base.length < 8 ? (base + random).slice(0, 8) : base;
  };

  const onSubmit = async (data: any) => {
    try {
      const q = query(collection(db, 'asesor'), where('correo', '==', data.correo));
      const existing = await getDocs(q);
      if (!existing.empty) {
        Swal.fire({
          icon: 'error',
          title: 'Correo duplicado',
          text: 'Ya existe un asesor con este correo.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.correo,
        data.contraseña
      );

      const asesor_id = generarID(data.nombre, data.inmobiliaria_id, data.años_experiencia);

      const inmobiliariaDoc = await getDoc(docRef(db, 'inmobiliaria', data.inmobiliaria_id));
      if (!inmobiliariaDoc.exists()) {
        Swal.fire({
          icon: 'error',
          title: 'Inmobiliaria no encontrada',
          text: 'No se encontró la inmobiliaria seleccionada.',
          confirmButtonColor: '#d33'
        });
        return;
      }
      const inmobiliariaData = inmobiliariaDoc.data();
      const inmobiliariaIDPersonalizado = inmobiliariaData?.inmobiliaria_id;

const newAsesor = {
  tipo: 'asesor',
  asesor_id,
  nombre: data.nombre,
  biografia: data.biografia,
  telefono: data.telefono,
  correo: data.correo,
  inmobiliaria_doc_ids: [data.inmobiliaria_id], // Usar doc.id de Firestore en array
  anos_experiencia: data.años_experiencia,
  redes: data.redes,
  foto: fotoUrl || '',
  certificaciones: [],
  calificacion: 0,
  activo: true
};


      await addDoc(collection(db, 'asesor'), newAsesor);

      const inmobiliariaRef = docRef(db, 'inmobiliaria', data.inmobiliaria_id);
      const snap = await getDoc(inmobiliariaRef);
      if (snap.exists()) {
        const existingAsesores = snap.data().asesor_id || [];
        const actualizado = Array.from(new Set([...existingAsesores, asesor_id]));
        await updateDoc(inmobiliariaRef, {
          asesor_id: actualizado
        });
      }

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Afiliado registrado exitosamente',
        confirmButtonColor: '#3085d6'
      });
      reset();
      setFotoUrl('');
    } catch (error: any) {
      console.error('Error al registrar afiliado:', error);
      const errorMessage = error.code ? getFirebaseErrorMessage(error.code) : error.message;
      Swal.fire({
        icon: 'error',
        title: 'Error al registrar',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
    }
  };

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">Registrar Afiliado</h2>
              <span className="ipn-subtitle">Completa el formulario para agregar un nuevo afiliado al sistema.</span>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Administración de Afiliados', path: '/admin-afiliado' },
              { label: 'Registrar Afiliado' }
            ]}
          />
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit(onSubmit, onError)} className="taller-form-modern">
                  {/* Sección 1: Datos Personales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Datos Personales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-6">
                    <label>Nombre completo <span className="text-danger">*</span></label>
                    <input {...register('nombre')} className="form-control" />
                    <small className="text-danger">{errors.nombre?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Correo <span className="text-danger">*</span></label>
                    <input {...register('correo')} className="form-control" type="email" />
                    <small className="text-danger">{errors.correo?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Teléfono <span className="text-danger">*</span></label>
                    <input {...register('telefono')} className="form-control" />
                    <small className="text-danger">{errors.telefono?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Años de experiencia <span className="text-danger">*</span></label>
                    <input {...register('años_experiencia')} className="form-control" type="number" />
                    <small className="text-danger">{errors.años_experiencia?.message}</small>
                  </div>
                  <div className="form-group col-md-12">
                    <label>Biografía <span className="text-danger">*</span></label>
                    <textarea {...register('biografia')} className="form-control h-120" />
                    <small className="text-danger">{errors.biografia?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Contraseña <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <input
                        {...register('contraseña')}
                        className="form-control"
                        type={showPassword ? "text" : "password"}
                        onChange={(e) => setContraseña(e.target.value)}
                        style={{ paddingRight: '45px' }}
                        placeholder="Ingresa tu contraseña"
                      />
                      <button
                        type="button"
                        className="btn position-absolute border-0"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          right: '5px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0.375rem 0.75rem',
                          zIndex: 10,
                          background: 'transparent'
                        }}
                        title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                      </button>
                    </div>
                    <small className="text-danger">{errors.contraseña?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Confirmar Contraseña <span className="text-danger">*</span></label>
                    <div className="position-relative">
                      <input
                        {...register('confirmar')}
                        className="form-control"
                        type={showConfirmPassword ? "text" : "password"}
                        onChange={(e) => setConfirmar(e.target.value)}
                        style={{ paddingRight: '45px' }}
                        placeholder="Confirma tu contraseña"
                      />
                      <button
                        type="button"
                        className="btn position-absolute border-0"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          right: '5px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          padding: '0.375rem 0.75rem',
                          zIndex: 10,
                          background: 'transparent'
                        }}
                        title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                      </button>
                    </div>
                    {confirmar && (
                      <div className="mt-1">
                        {contraseña === confirmar ? (
                          <small className="text-success">
                            ✅ Las contraseñas coinciden
                          </small>
                        ) : (
                          <small className="text-danger">
                            ❌ Las contraseñas no coinciden
                          </small>
                        )}
                      </div>
                    )}
                    <small className="text-danger">{errors.confirmar?.message}</small>
                  </div>
                  <div className="form-group col-md-12">
                    <label>Selecciona Inmobiliaria <span className="text-danger">*</span></label>
                    <select {...register('inmobiliaria_id')} className="form-control">
                      <option value="">Selecciona una</option>
                      {inmobiliarias.map((inm) => (
                        <option key={inm.id} value={inm.id}>
                          {inm.nombre}
                        </option>
                      ))}
                    </select>
                    <small className="text-danger">{errors.inmobiliaria_id?.message}</small>
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Foto de Perfil */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Foto de Perfil</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-12">
                    <label>Foto de perfil del afiliado</label>
                    <PhotoUpload
                      onUpload={(urls: string[]) => {
                        if (urls.length > 0) {
                          setFotoUrl(urls[0]);
                        }
                      }}
                      maxFiles={1}
                      storageFolder="asesores/perfil"
                      allowedTypes={['image/jpeg', 'image/png', 'image/webp']}
                      maxSizeMB={5}
                      className="agent-profile-upload"
                    />
                    {fotoUrl && (
                      <div className="mt-3">
                        <img 
                          src={fotoUrl} 
                          alt="Vista previa" 
                          className="img-thumbnail"
                          style={{ maxWidth: '150px', maxHeight: '150px' }}
                        />
                      </div>
                    )}
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 3: Redes Sociales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">3. Redes Sociales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-6">
                    <label>Facebook</label>
                    <input {...register('redes.facebook')} className="form-control" />
                    <small className="text-danger">{errors.redes?.facebook?.message}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Instagram</label>
                    <input {...register('redes.instagram')} className="form-control" />
                    <small className="text-danger">{errors.redes?.instagram?.message}</small>
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de submit */}
                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary px-5">
                      <i className="fa fa-user-plus me-2"></i>
                      Registrar Afiliado
                      <i className="fa-solid fa-arrow-right-long ms-2"></i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
